import apiClient from '../service/api'

/**
 * 어드민 운영지표 API (aivatar#283 Phase 1)
 * - 기간 from/to는 ISO date(YYYY-MM-DD), 둘 다 포함(inclusive)
 * - 응답은 DTO 직접 반환 (promotions와 달리 data envelope 없음)
 */

export type Granularity = 'DAY' | 'WEEK' | 'MONTH'

export interface MetricsPeriod {
  from: string
  to: string
}

// ── revenue ──────────────────────────────────────────────

export interface PlanDistribution {
  planName: string
  level: number | null
  workspaceCount: number
}

export interface MrrSummary {
  currencyCode: string | null
  manualPayment: boolean
  mrr: number
  arr: number
}

export interface RevenueMetrics {
  activePaidWorkspaces: number
  newPaidWorkspaces: number
  churnedPaidWorkspaces: number
  /** 기간 시작 시점 활성 유료 base (aivatar#297) */
  activePaidAtStart: number
  /** 유료 구독 이탈률 0~1 — base 0이면 null (aivatar#297) */
  churnRate: number | null
  planDistribution: PlanDistribution[]
  mrr: MrrSummary[]
}

export const getRevenueMetrics = async ({ from, to }: MetricsPeriod): Promise<RevenueMetrics> => {
  const { data } = await apiClient.get('/v1/admin/metrics/revenue', { params: { from, to } })
  return data
}

// ── credits ──────────────────────────────────────────────

export interface CreditTrendPoint {
  bucket: string
  charged: number
  consumed: number
}

export interface CreditMetrics {
  trend: CreditTrendPoint[]
  totalCharged: number
  totalConsumed: number
}

export const getCreditMetrics = async ({
  from,
  to,
  granularity,
}: MetricsPeriod & { granularity: Granularity }): Promise<CreditMetrics> => {
  const { data } = await apiClient.get('/v1/admin/metrics/credits', { params: { from, to, granularity } })
  return data
}

// ── payments ─────────────────────────────────────────────

export interface PaymentTrendPoint {
  bucket: string
  currencyCode: string | null
  paidCount: number
  paidAmount: number
}

export interface PaidTotal {
  currencyCode: string | null
  paidCount: number
  paidAmount: number
}

export interface FailureReason {
  message: string | null
  count: number
}

export interface PaymentMetrics {
  trend: PaymentTrendPoint[]
  statusCounts: Record<string, number>
  totals: PaidTotal[]
  successRate: number | null
  topFailureReasons: FailureReason[]
}

export const getPaymentMetrics = async ({
  from,
  to,
  granularity,
}: MetricsPeriod & { granularity: Granularity }): Promise<PaymentMetrics> => {
  const { data } = await apiClient.get('/v1/admin/metrics/payments', { params: { from, to, granularity } })
  return data
}

// ── usage ────────────────────────────────────────────────

export interface ProcessingTime {
  avgSeconds: number | null
  p50Seconds: number | null
  p90Seconds: number | null
}

export interface ServiceUsage {
  statusCounts: Record<string, number>
  total: number
  processingTime: ProcessingTime | null
  processingTimeApproximate: boolean
}

export interface AiImageUsage {
  statusCounts: Record<string, number>
  total: number
  requestedImages: number
  completedImages: number
  processingTime: ProcessingTime | null
}

export interface NewsUsage {
  total: number
  consumedCredits: number
}

export interface UsageMetrics {
  vgenVideo: ServiceUsage
  aivatarVideo: ServiceUsage
  aiImage: AiImageUsage
  news: NewsUsage
}

export const getUsageMetrics = async ({ from, to }: MetricsPeriod): Promise<UsageMetrics> => {
  const { data } = await apiClient.get('/v1/admin/metrics/usage', { params: { from, to } })
  return data
}

// ── activation ───────────────────────────────────────────

/** @deprecated .toHours() 정수 절삭이라 1시간 미만이 0으로 뭉개짐 — minutesToFirstVideo 사용 (aivatar#230) */
export interface HoursToFirstVideo {
  avgHours: number | null
  p50Hours: number | null
  p90Hours: number | null
}

export interface MinutesToFirstVideo {
  avgMinutes: number | null
  p50Minutes: number | null
  p90Minutes: number | null
}

export interface ActivationMetrics {
  signups: number
  activatedWithinWindow: number
  windowDays: number
  activationRate: number
  convertedToPaidWithinWindow: number
  paidWindowDays: number
  paidConversionRate: number
  /** 첫 영상 "요청까지" 소요시간(완료 아님). 백엔드 분 단위 전환 후 채워짐 */
  minutesToFirstVideo?: MinutesToFirstVideo | null
  /** @deprecated 시간 절삭본 — minutesToFirstVideo 없을 때만 폴백 */
  hoursToFirstVideo: HoursToFirstVideo | null
}

export const getActivationMetrics = async ({
  from,
  to,
  windowDays = 7,
  paidWindowDays = 30,
}: MetricsPeriod & { windowDays?: number; paidWindowDays?: number }): Promise<ActivationMetrics> => {
  const { data } = await apiClient.get('/v1/admin/metrics/activation', {
    params: { from, to, windowDays, paidWindowDays },
  })
  return data
}

// ── retention (가입 코호트 × 주차) ─────────────────────────

export interface RetentionWeek {
  weekNumber: number
  activeUsers: number
  retentionRate: number
}

export interface RetentionCohort {
  cohort: string
  signups: number
  /** 아직 완전히 경과하지 않은 주차는 빠짐 — 길이가 코호트마다 다름 */
  weeks: RetentionWeek[]
}

export interface RetentionMetrics {
  cohorts: RetentionCohort[]
}

export const getRetentionMetrics = async ({
  from,
  to,
  maxWeeks = 8,
}: MetricsPeriod & { maxWeeks?: number }): Promise<RetentionMetrics> => {
  const { data } = await apiClient.get('/v1/admin/metrics/retention', { params: { from, to, maxWeeks } })
  return data
}
